import React, { useState } from 'react';
import { useRouter } from 'next/router';
import analyticsTrack from '@lib/analytics/analyticsTrack';
import { ParsedUrlQueryInput, stringify as urlQueryStringify } from 'querystring';
import {
  IFacet,
  IFacetValue,
  parsePriceFacetValue
} from '../../../model/SearchResults';
import styles from './FacetFilter.module.scss';
import AnimatedHandle from '../../Sidebar/components/Drawer/AnimatedHandle';

interface IFacetProps {
  facet: IFacet;
  className?: string;
}

const FacetFilter = ({
  className,
  facet,
}: IFacetProps) => {
  const router = useRouter();
  const query: ParsedUrlQueryInput = { ...router.query };

  const initCollapse = (): boolean => {
    if (facet.name === 'price') return !!query.priceMin || !!query.priceMax;
    return !!query[facet.name];
  };

  const [isOpen, setIsOpen] = useState(initCollapse());
  const [isCollapsed, setIsCollapsed] = useState(!initCollapse());

  const handleFacetClick = () => {
    if (isOpen) {
      setIsOpen(false);
      setTimeout(() => setIsCollapsed(true), 250);
    } else {
      setIsCollapsed(false);
      setTimeout(() => setIsOpen(true), 100);
    }
  };

  function urlForFacet(value: IFacetValue) {
    if (facet.name === 'price') {
      /* eslint-disable no-unused-expressions */
      const [lower, upper] = parsePriceFacetValue(value);
      (lower && (query.priceMin = lower)) || delete query.priceMin;
      (upper && (query.priceMax = upper)) || delete query.priceMax;
      /* eslint-enable no-unused-expressions */
    } else {
      query[facet.name] = value.id;
    }

    return `${router.pathname}?${urlQueryStringify(query)}`;
  }

  function isValueSelected(value: IFacetValue) {
    if (facet.name === 'price') {
      const [lower, upper] = parsePriceFacetValue(value);
      return (
        ((lower && router.query.priceMin === lower) || (!lower && !router.query.priceMin)) &&
        ((upper && router.query.priceMax === upper) || (!upper && !router.query.priceMax))
      );
    } else {
      const facetValue = router.query[facet.name];
      return (facetValue === value.value || facetValue === value.id);
    }
  }

  function deselectFilter(name: string) {
    const { push, pathname } = router;

    if (name === 'price') {
      delete query.priceMin; delete query.priceMax;
    } else {
      delete query[name];
    }

    push({ pathname, query });
  }

  function navigate(facetName: string, value: IFacetValue): void {
    analyticsTrack('shop_filter_clicked');
    if (isValueSelected(value)) {
      deselectFilter(facetName);
    } else {
      router.push(urlForFacet(value));
    }
  }

  return (
    <div className={`${styles.Root} ${className}`}>
      <div className={styles.FilterButton}>
        <AnimatedHandle
          handleDrawerOpened={handleFacetClick}
          isActive={isOpen}
          title={facet.name}
        />
      </div>
      <ul className={`${styles.FilterList} ${styles.TopLevel} ${isOpen ? styles.Expanded : ''} ${isCollapsed ? styles.Collapsed : ''}`}>
        {
          facet.values.map((value) => (
            <li
              key={value.id}
              className={`${styles.FilterItem} ${isValueSelected(value) ? styles.Selected : ''}`}
            >
              <button
                type="button"
                className={ styles.Button }
                onClick={() => navigate(facet.name, value)}
              >
                { value.value }
                <span className="havenly-icon close" />
              </button>
            </li>
          ))
        }
      </ul>
      <div className={styles.Divider} />
    </div>
  );
};

export default FacetFilter;

